// src/pages/UnauthorizedPage.js - Updated for global permission system
import React, { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaLock, FaHome, FaArrowLeft, FaExclamationTriangle } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';

const UnauthorizedPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, homePath } = useContext(AuthContext);
  
  const from = location.state?.from;
  const attemptedPath = from?.pathname && from.pathname !== '/' ? from.pathname : null;
  const message = "Your group doesn't have access to this page.";
  const pageInfo = attemptedPath
    ? `You attempted to open ${attemptedPath}.`
    : "You attempted to access a restricted page.";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-lg w-full space-y-8">
        {/* Main Card */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="px-6 sm:px-10 py-8 text-center">
            {/* Lock Icon */}
            <div className="mb-6 flex justify-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <FaLock className="w-8 h-8 text-red-600" />
              </div>
            </div>
            
            {/* Title */}
            <h2 className="mb-4 text-3xl font-bold text-gray-900">
              Access Denied
            </h2>
            
            {/* Main Message */}
            <p className="mb-6 text-lg text-gray-700">
              {message}
            </p>
            
            {/* Page Info */}
            <p className="mb-6 text-sm text-gray-600">
              {pageInfo}
            </p>
            
            {/* User Status Alert */}
            {user ? (
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-center justify-center">
                  <FaExclamationTriangle className="text-blue-600 mr-2" />
                  <div className="text-sm text-blue-800">
                    You are logged in as <span className="font-semibold">{user.username}</span> but your group hasn't been given this page.
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <div className="flex items-center justify-center">
                  <FaExclamationTriangle className="text-yellow-600 mr-2" />
                  <div className="text-sm text-yellow-800">
                    You need to log in to access this page.
                  </div>
                </div>
              </div>
            )}
            
            {/* Contact Admin Message */}
            <p className="mb-8 text-sm text-gray-600">
              If you need this page, ask an administrator to open it for your group in Permission Control.
            </p>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-3">
              <button 
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-200 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft className="mr-2 h-4 w-4" />
                Go Back
              </button>
              
              {user ? (
                <Link 
                  to={homePath}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  <FaHome className="mr-2 h-4 w-4" />
                  Home page
                </Link>
              ) : (
                <Link 
                  to="/login" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                  state={{ from }}
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnauthorizedPage;